import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    useTheme, useMediaQuery, Box, Typography, TextField, Button,   
    CircularProgress, FormControlLabel, Checkbox, MenuItem,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const ChecklistItemPage = () => {
    useAuthCheck();
    const { checklistItemId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); 

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);    
    const [formValues, setFormValues] = useState({
        code: '',
        description: '',
        severity: 'info',
        sortOrder: 0,
        isActive: true,
    });


    useEffect(() => {     
        const fetchChecklistItem = async () => {
            if (!checklistItemId) return;

            try {
                setLoading(true);
                const res = await api.get(`/checklistitems/${checklistItemId}`);
                const data = res.data;
                
                setFormValues({
                    code: data.code || '',
                    description: data.description || '',
                    severity: data.severity || 'info',
                    sortOrder: data.sortOrder || 0,
                    isActive: data.isActive ?? true,
                });
            } catch (err) {
                setError(err);
                toast.error("Failed to load data");
            } finally {
                setLoading(false);
            }
        };
        fetchChecklistItem();
    }, [checklistItemId]);
    
    const validationSchema = yup.object().shape({
        code: yup.string().required('Checklist code is required.'),
        description: yup.string().required('Description is required.'),
        severity: yup.string().oneOf(['info', 'warning', 'critical']).required(),
        sortOrder: yup.number().typeError('Must be a number.').min(0),
    });

    const handleFormSubmit = async (values) => {
        try {
            const isUpdate = !!checklistItemId;
            const url = isUpdate 
                ? `/checklistitems/update/${checklistItemId}` 
                : '/checklistitems/new';

            await api({
                method: isUpdate ? "put" : "post",
                url,
                data: values,
            });

            toast.success(`Checklist item ${isUpdate ? 'updated' : 'created'} successfully`);
            navigate("/checklistitems");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Operation failed!");
            setError(err);
        }
    };

    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Checklist Item"
                    subtitle={loading ? 'Loading...' : checklistItemId ? 'Update' : 'Create'}
                />
                {!loading && (
                    <StyledButton onClick={() => navigate("/checklistitems")}>
                        <ArrowBackIosNewIcon />
                    </StyledButton>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}><CircularProgress /></FlexCenter>
            ) : error && !formValues.code ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || 'Something went wrong'}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize={true}
                        validationSchema={validationSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({ 
                            values, 
                            errors, 
                            touched, 
                            handleBlur, 
                            handleChange, 
                            handleSubmit 
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    sx={{ '& > div': { gridColumn: isDesktop ? undefined : 'span 12' } }}
                                >
                                    {/* CODE */}
                                    <TextField
                                        fullWidth 
                                        required 
                                        variant="filled" 
                                        label="Item Code"
                                        name="code" 
                                        value={values.code}
                                        onBlur={handleBlur} 
                                        onChange={handleChange}
                                        error={!!touched.code && !!errors.code}
                                        helperText={touched.code && errors.code}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    {/* LABEL */}
                                    <TextField
                                        fullWidth 
                                        required 
                                        variant="filled" 
                                        label="Description"
                                        name="description" value={values.description}
                                        onBlur={handleBlur} onChange={handleChange}
                                        error={!!touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    {/* SEVERITY */}
                                    <TextField
                                        select 
                                        fullWidth 
                                        variant="filled" 
                                        label="Severity"
                                        name="severity" 
                                        value={values.severity}
                                        onChange={handleChange} 
                                        onBlur={handleBlur}
                                        error={!!touched.severity && !!errors.severity}
                                        sx={{ gridColumn: 'span 6' }}
                                    >
                                        <MenuItem value="info">Info</MenuItem>
                                        <MenuItem value="warning">Warning</MenuItem>
                                        <MenuItem value="critical">Critical</MenuItem>
                                    </TextField>

                                    {/* SORT ORDER */}
                                    <TextField
                                        fullWidth 
                                        variant="filled" 
                                        type="number" 
                                        label="Sort Order"
                                        name="sortOrder" 
                                        value={values.sortOrder}
                                        onBlur={handleBlur} 
                                        onChange={handleChange}
                                        slotProps={{ inputProps: { min: 0 } }}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    {/* IS ACTIVE */}
                                    <FormControlLabel
                                        label="Is Active"
                                        control={
                                            <Checkbox 
                                                name="isActive" 
                                                checked={values.isActive} 
                                                onChange={handleChange} 
                                                color="secondary"
                                            />
                                        }
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <Button
                                        fullWidth 
                                        type="submit" 
                                        variant="contained" 
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {checklistItemId ? "Update" : "Create"}
                                    </Button>                                    
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default ChecklistItemPage;